/**
 * Step 1 - Product Only (no host)
 *
 * Full-width product selection with warranty + additional details.
 * Used when the product template has no host_template (unhosted products).
 * Progressive disclosure: product card → additional details (after warranty resolved).
 */

import type { WizardState } from '@/admin/pages/registrations/new-registration-page'
import type { RegistrationFormMetadata } from '@/admin/types/products'
import {
  ProductPanel,
  AdditionalDetailsPanel,
} from './shared'
import { RegistrationStepActions, RegistrationStepGrid } from './layout'
import { useRegisterValidator, useWizardValidation } from './wizard-validation'

interface StepProps {
  state: WizardState
  update: (payload: Partial<WizardState>) => void
  next: () => void
  back?: () => void
  continueDisabled?: boolean
  continueHint?: string | null
  metadata: RegistrationFormMetadata | null
  /**
   * Extra panel(s) to render alongside the step's own panels in the
   * shared `RegistrationStepGrid`.
   */
  extraPanels?: React.ReactNode
  /**
   * Use the flat (category-less) product picker variant. Selecting a
   * parent product drills into a variant picker. Forwarded to
   * `ProductPanel`.
   */
  flatProductSelection?: boolean
  /**
   * When set, only products from the named product template are shown.
   * Forwarded to `ProductPanel`.
   */
  productTemplateName?: string | null
}

export function StepProductOnly({
  state,
  update,
  next,
  back,
  // The legacy `continueDisabled` prop is intentionally not
  // destructured. The Continue button is always enabled and validation
  // happens via the wizard validation context.
  continueHint,
  metadata,
  extraPanels,
  flatProductSelection = false,
  productTemplateName,
}: StepProps) {
  const showDetails = !!state.selectedProduct
  const productSelected = !!state.selectedProduct

  // Register the "must select a product" check with the wizard
  // validation context. When the user clicks Continue without picking
  // a product the click is intercepted and `attempted` flips, which
  // we consume below to render the inline product-selection error.
  useRegisterValidator('product:selected', productSelected)
  const { attempted } = useWizardValidation()
  const productError = attempted && !productSelected
    ? 'Select a product to continue'
    : null

  return (
    <div className="flex h-full flex-col">
      <GeoAvailabilityBanner state={state} />

      <RegistrationStepGrid>
        <ProductPanel
          state={state}
          update={update}
          detectedCategory={null}
          metadata={metadata}
          showWarrantySelector={false}
          flatProductSelection={flatProductSelection}
          productTemplateName={productTemplateName}
          requiredError={productError}
        />

        {showDetails && (
          <div className="panel-arrive flex flex-col gap-6">
            {metadata && (
              <AdditionalDetailsPanel state={state} update={update} metadata={metadata} />
            )}
            {extraPanels}
          </div>
        )}
      </RegistrationStepGrid>

      <RegistrationStepActions
        onContinue={next}
        onBack={back}
        continueHint={continueHint}
        className="panel-arrive mt-4"
      />
    </div>
  )
}

/**
 * Geo availability banner.
 *
 * Surfaces the outcome of the host_context step's PSA + window resolution
 * so the user understands why the product picker is narrowed (or empty):
 * - PSA resolved + products available → green "showing N products" banner
 * - PSA resolved + zero products       → amber banner explaining the
 *   PSA + installation date returned nothing eligible (this is the case
 *   where only the global filter rules used to apply because the panel
 *   silently fell back to the unfiltered catalogue)
 * - PSA could not be resolved          → amber banner explaining the
 *   address didn't match any active PSA, so no products can be shown
 */
function GeoAvailabilityBanner({ state }: { state: WizardState }) {
  const eligibility = state.geoEligibility
  if (!eligibility) return null

  // When the scoped product template opts out of PSA (geo) filtering, the
  // product list is the template's catalogue - not a geo-narrowed set - so
  // none of the PSA-specific banners apply.
  if (!eligibility.psaFilteringApplied) return null

  if (!eligibility.psa) {
    return (
      <div className="mb-4 flex items-center gap-2 rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--status-warn-bg)] px-3 py-2 text-[11px] text-[var(--status-warn)]">
        We couldn&rsquo;t match this address to a Plant Service Area, so no products are available. Verify the postcode and try again.
      </div>
    )
  }

  if (eligibility.productCount === 0) {
    return (
      <div className="mb-4 flex items-center gap-2 rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--status-warn-bg)] px-3 py-2 text-[11px] text-[var(--status-warn)]">
        No products are currently available in {eligibility.psa.name} on the chosen installation date.
        {eligibility.confidence === 'state_default' && ' (state default - verify address)'}
      </div>
    )
  }

  return (
    <div className="mb-4 flex items-center gap-2 rounded-[4px] border-[0.5px] border-[var(--green)] bg-[var(--green-bg)] px-3 py-2 text-[11px] text-[var(--green)]">
      Showing {eligibility.productCount} product{eligibility.productCount !== 1 ? 's' : ''} available
      for {eligibility.psa.name}
      {eligibility.confidence === 'state_default' && ' (state default - verify address)'}
    </div>
  )
}
